import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import type { ClassSelect } from '@org/model';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useClassBlocks, StudentExerciseContent } from '../hooks/use-class-blocks';
import {
  useContentRenderer,
  ExerciseSubmission,
} from '../hooks/use-content-renderer';
import { useExerciseManager } from '../hooks/use-exercise-manager';
import {
  deriveBlockDone,
  deriveBlockKind,
  deriveBlockMinutes,
} from '../hooks/zine-derive';
import { ContentType } from '../../../types/contents';
import { unwrapTrpcData } from '../../../utils';
import { ClassBlockList } from '../components/class-block-list';
import { usePageContext } from '../../chat/chat-context';
import { ProtectedContentShell } from '../../security/protected-content-shell';
import { useAuthorization } from '../../login/use-authorization';
import {
  ZineIssue,
  ZineCover,
  AgendaBand,
  BackCover,
  BlockNavPill,
  type ZineAgendaItem,
  type ZineStamp,
  type BackCoverPoster,
  zineZeroPad,
} from '../zine';
import type { LatestSubmissionResult } from '../types/exercise';
import { useFeatureFlag } from '../../../feature-flags/use-feature-flag';
import { ClassDetailPageLegacy } from '../legacy/class-detail';

interface ClassDetailPageProps {
  classId: string;
  blockIdx?: number;
  mode?: 'student' | 'teacher';
  viewerStudentId?: string;
  isReadOnly?: boolean;
  /**
   * Fired from the back cover's finish action when that class completes the
   * whole journey. The host route navigates to the wall so the last unit's
   * selo ceremony plays first and the journey celebration follows it.
   */
  onJourneyComplete?: (info: {
    previousJourneyId: string;
    nextJourneyId: string | null;
    unitId: string;
  }) => void;
  /**
   * Fired (debounced) when the student settles on a different spread while
   * scrolling. The p2e route uses it to keep the /block/$blockIdx URL in sync
   * (replace navigation) so a refresh reopens the spread they were reading.
   */
  onBlockIdxChange?: (blockIdx: number) => void;
  /**
   * Exercise to land on when the page opens (a notification about a teacher
   * comment deep-links here). Scrolls to the exercise instead of the spread
   * top; the exercise must be inside the `blockIdx` spread.
   */
  scrollToExerciseId?: string;
}

/**
 * Minimal glue styles the zine stylesheet doesn't cover:
 * - markdown headings / blockquotes inside `.z-prose`
 * - the unobtrusive restart action in the cover masthead
 * - spacing for full-width exercise slots below the spread columns
 */
const ZINE_GLUE_CSS = `
.z-issue .z-prose h1, .z-issue .z-prose h2, .z-issue .z-prose h3, .z-issue .z-prose h4 {
  font-family: var(--z-font-cond); text-transform: uppercase; letter-spacing: .02em;
  line-height: 1.05; margin: 1.2rem 0 .6rem; text-wrap: balance;
}
.z-issue .z-prose h1 { font-size: 1.6rem; }
.z-issue .z-prose h2 { font-size: 1.4rem; }
.z-issue .z-prose h3 { font-size: 1.2rem; }
.z-issue .z-prose h4 { font-size: 1.05rem; }
.z-issue .z-prose blockquote {
  border-left: 3px solid var(--z-charm); margin: 0 0 1rem; padding: .2rem 0 .2rem 1rem;
  font-family: var(--z-font-serif); font-style: italic; color: var(--z-mid);
}
.z-issue .z-prose blockquote p { margin: 0 0 .4rem; }
.z-issue .z-prose img { max-width: 100%; height: auto; }
.z-issue .z-restart {
  background: none; border: 0; padding: 0; color: inherit; font: inherit;
  letter-spacing: inherit; text-transform: uppercase; cursor: pointer;
  opacity: .75; text-decoration: underline; text-underline-offset: 3px;
}
.z-issue .z-restart:hover { opacity: 1; }
.z-issue .z-restart:disabled { opacity: .4; cursor: default; }
.z-issue .z-exercise { margin-top: 1.8rem; }
.z-issue .z-exercise .z-attempt { display: block; margin-bottom: .4rem; }
/* Arrival flash for a deep-linked exercise (teacher comment notification). */
.z-issue .z-exercise--target { border-radius: 6px; animation: z-exercise-target 2.4s ease-out 1; }
@keyframes z-exercise-target {
  0%   { box-shadow: 0 0 0 0 rgba(242,160,196,.95); }
  18%  { box-shadow: 0 0 0 8px rgba(242,160,196,.55); }
  100% { box-shadow: 0 0 0 10px rgba(242,160,196,0); }
}
.z-issue .z-media { margin: 1.4rem 0; }
.z-issue .z-state-page { padding-top: 4.5rem; padding-bottom: 4.5rem; }
`;

const EMPTY_IDS: string[] = [];
const AGENDA_SUBTITLE_MAX = 80;
/** Deep-link landing: how long to wait for the target to exist in the DOM. */
const DEEP_LINK_WAIT_MS = 3000;
/** Deep-link landing: how long to keep re-anchoring while the layout settles. */
const DEEP_LINK_SETTLE_MS = 900;
/** Deep-link landing: how long the arrival ring stays on the exercise. */
const DEEP_LINK_FLASH_MS = 2600;
/** Deep-link landing: breathing room between the viewport edge and the target. */
const DEEP_LINK_GAP_PX = 16;

/** Nearest ancestor that actually scrolls, or null when the window does. */
function findScrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * The visible band of the scrolling viewport: its top is pushed down by
 * whatever sticky/fixed bar overlays it (the app's top navbar), which
 * `scrollIntoView` ignores — that is how a teacher's note ended up tucked
 * under the bar, hidden above the fold.
 */
function visibleBand(scroller: HTMLElement | null): { top: number; bottom: number } {
  const rect = scroller ? scroller.getBoundingClientRect() : null;
  const viewportTop = rect ? rect.top : 0;
  const viewportBottom = rect ? rect.bottom : window.innerHeight;
  let top = viewportTop;
  document.querySelectorAll<HTMLElement>('header, nav').forEach((bar) => {
    const { position } = getComputedStyle(bar);
    if (position !== 'sticky' && position !== 'fixed') {
      return;
    }
    const barRect = bar.getBoundingClientRect();
    if (barRect.top <= viewportTop + 1 && barRect.bottom > top) {
      top = barRect.bottom;
    }
  });
  return { top, bottom: viewportBottom };
}

function scrollBy(scroller: HTMLElement | null, delta: number) {
  if (Math.abs(delta) < 1) {
    return;
  }
  if (scroller) {
    scroller.scrollBy({ top: delta });
  } else {
    window.scrollBy({ top: delta });
  }
}

/** Scroll so the target's top sits just below the sticky top bar. */
function alignBelowStickyBars(target: HTMLElement, gap: number) {
  const scroller = findScrollParent(target);
  const band = visibleBand(scroller);
  scrollBy(scroller, target.getBoundingClientRect().top - band.top - gap);
}

/**
 * Scroll so the target's bottom sits at the bottom of the view, leaving the
 * screen above it to the exercise it belongs to — the student sees their
 * answer and, right under it, the teacher's note. A note taller than the
 * view is aligned to the top instead so it reads from the beginning.
 */
function alignAtBottom(target: HTMLElement, gap: number) {
  const scroller = findScrollParent(target);
  const band = visibleBand(scroller);
  const rect = target.getBoundingClientRect();
  if (rect.height + gap * 2 > band.bottom - band.top) {
    scrollBy(scroller, rect.top - band.top - gap);
    return;
  }
  scrollBy(scroller, rect.bottom - (band.bottom - gap));
}

function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Dispatches between the two class-page designs. The zine issue is behind the
 * `zine-class-page` feature flag (per-user DB flag, or admin:all); everyone
 * else gets the legacy page. Teacher views always get the legacy page — the
 * full-bleed zine layout clashes with the teacher app's framed student view.
 * When the zine becomes the only design, delete `../legacy` and inline
 * ZineClassDetailPage here.
 */
export function ClassDetailPage(props: ClassDetailPageProps) {
  const { isEnabled, isLoading } = useFeatureFlag('zine-class-page');
  if (props.mode === 'teacher') {
    return <ClassDetailPageLegacy {...props} />;
  }
  if (isLoading) {
    return null;
  }
  return isEnabled ? (
    <ZineClassDetailPage {...props} />
  ) : (
    <ClassDetailPageLegacy {...props} />
  );
}

function ZineClassDetailPage({
  classId,
  blockIdx,
  mode = 'student',
  viewerStudentId,
  isReadOnly = false,
  onJourneyComplete,
  onBlockIdxChange,
  scrollToExerciseId,
}: ClassDetailPageProps) {
  const { t } = useTranslation('p2e');
  const navigate = useNavigate();
  const isTeacherView = mode === 'teacher';
  const isStudentSession = !isReadOnly && !isTeacherView;
  const normalizedBlockIdx = useMemo(() => {
    if (blockIdx === undefined || Number.isNaN(blockIdx)) {
      return undefined;
    }
    return blockIdx;
  }, [blockIdx]);

  // The URL's blockIdx is an ENTRY POINT only: it drives the initial scroll
  // and initial visit record. Later prop changes are our own scroll→URL syncs
  // (see onBlockIdxChange) and must not re-scroll or re-record.
  const [initialBlockIdx] = useState(normalizedBlockIdx);

  // Block currently in the reading band — drives the folio pill.
  const [currentBlockIdx, setCurrentBlockIdx] = useState(initialBlockIdx ?? 0);
  // While the top-of-issue agenda is on screen the pill would duplicate it.
  const [agendaInView, setAgendaInView] = useState(true);

  // Keep the latest callback in a ref so the scroll tracker doesn't
  // re-subscribe when a parent passes a new function identity.
  const onBlockIdxChangeRef = useRef(onBlockIdxChange);
  onBlockIdxChangeRef.current = onBlockIdxChange;

  // The issue page renders the WHOLE class: fetch every block (no blockIdx
  // filter — blocks.getBlocksByClass returns all blocks when idx is omitted).
  const { blocks, isLoading: isBlocksLoading } = useClassBlocks({ classId });

  const sortedBlocks = useMemo(
    () => blocks.slice().sort((a, b) => a.order - b.order),
    [blocks],
  );

  // Record visit on mount
  const { mutate: recordVisit } = trpc.classes.recordClassVisit.useMutation();

  // Sections the teacher marked as homework for this reader's class session(s).
  const { data: homeworkBlocksData } = trpc.classes.homeworkBlocks.useQuery(
    { classId, studentId: viewerStudentId },
    { enabled: Boolean(classId), staleTime: 60_000 },
  );
  const homeworkBlockIds = homeworkBlocksData?.blockIds ?? EMPTY_IDS;
  // Last block recorded by either the mount effect or the scroll tracker.
  const lastRecordedKeyRef = useRef<string | null>(null);
  // Initial (URL-driven) record — its own ref, so scroll-tracking updates to
  // lastRecordedKeyRef can't make this effect re-record the URL block when
  // its deps churn identity on re-renders.
  const initialVisitKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!classId || !sortedBlocks.length || isReadOnly || isTeacherView) {
      return;
    }

    const activeBlock =
      initialBlockIdx !== undefined
        ? sortedBlocks[initialBlockIdx] ?? sortedBlocks[0]
        : sortedBlocks[0];

    if (!activeBlock) {
      return;
    }

    const key = `${classId}-${activeBlock.id}`;
    if (initialVisitKeyRef.current === key) {
      return;
    }

    recordVisit({
      classId,
      blockId: activeBlock.id,
    });
    initialVisitKeyRef.current = key;
    lastRecordedKeyRef.current = key;
  }, [classId, initialBlockIdx, sortedBlocks, recordVisit, isReadOnly, isTeacherView]);

  // Scroll-position visit tracking. The old per-page block navigation recorded
  // each block the student opened as their resume point; on the single-scroll
  // issue the equivalent is the spread currently in view. Observe every spread
  // and record it once it settles in the reading band of the viewport
  // (debounced so skimming past spreads doesn't record them).
  useEffect(() => {
    if (!classId || sortedBlocks.length === 0 || isReadOnly || isTeacherView) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const blockByElement = new Map<
      Element,
      { block: (typeof sortedBlocks)[number]; index: number }
    >();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const found = blockByElement.get(entry.target);
          if (!found) continue;
          const key = `${classId}-${found.block.id}`;
          if (lastRecordedKeyRef.current === key) continue;
          clearTimeout(settleTimer);
          settleTimer = setTimeout(() => {
            lastRecordedKeyRef.current = key;
            recordVisit({ classId, blockId: found.block.id });
            onBlockIdxChangeRef.current?.(found.index);
          }, 800);
        }
      },
      // "Reading band": a spread counts as current while it crosses the
      // upper-middle of the viewport.
      { rootMargin: '-30% 0px -55% 0px' },
    );

    sortedBlocks.forEach((block, index) => {
      const el = document.getElementById(`s${index + 1}`);
      if (el) {
        blockByElement.set(el, { block, index });
        observer.observe(el);
      }
    });

    return () => {
      clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, [classId, sortedBlocks, recordVisit, isReadOnly, isTeacherView]);

  // Folio pill support: track which block is in the reading band for every
  // zine session (the visit tracker above is student-only), and hide the pill
  // while the agenda band at the top of the issue is still on screen.
  useEffect(() => {
    if (sortedBlocks.length === 0 || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const indexByElement = new Map<Element, number>();
    const spreadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = indexByElement.get(entry.target);
          if (index !== undefined) setCurrentBlockIdx(index);
        }
      },
      { rootMargin: '-30% 0px -55% 0px' },
    );
    sortedBlocks.forEach((_block, index) => {
      const el = document.getElementById(`s${index + 1}`);
      if (el) {
        indexByElement.set(el, index);
        spreadObserver.observe(el);
      }
    });

    const agendaEl = document.querySelector('.z-agenda-band');
    const agendaObserver = new IntersectionObserver(
      ([entry]) => setAgendaInView(Boolean(entry?.isIntersecting)),
      { threshold: 0 },
    );
    if (agendaEl) {
      agendaObserver.observe(agendaEl);
    } else {
      setAgendaInView(false);
    }

    return () => {
      spreadObserver.disconnect();
      agendaObserver.disconnect();
    };
  }, [sortedBlocks]);

  // Resolve (journeyId, unitId) from the server — single source of truth,
  // derived from LearningProgress or UnitXClass∩UserXJourney.
  const { data: classContext } = trpc.progress.resolveClassContext.useQuery(
    { classId },
    {
      enabled: Boolean(classId) && isStudentSession,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
  const journeyId = classContext?.journeyId;
  const unitId = classContext?.unitId;
  // Purchased/granted access whose window has closed: the class stays
  // viewable (exercises included) but can't be finished or auto-completed.
  const journeyReadOnly = classContext?.journeyReadOnly ?? false;

  // Journey wall — powers the cover issue line / masthead and the back-cover
  // poster wall (earned unit cartazes + current class position in the unit).
  const { data: wallData } = trpc.progress.getStudentJourneyWall.useQuery(
    { journeyId: journeyId! },
    {
      enabled: Boolean(journeyId) && isStudentSession,
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    },
  );

  // Auto-complete class when student reaches the final block (D2)
  const utils = trpc.useUtils();
  // Journey completion is NOT acted on here: the auto-complete below fires the
  // moment the final spread is reached, and leaving the class then would skip
  // the back cover. handleFinishClass handles it on the student's own click.
  const completeClassMutation = trpc.progress.completeClass.useMutation({
    onSuccess: () => {
      utils.progress.getStudentJourneyWall.invalidate();
      utils.progress.getStudentJourneys.invalidate();
    },
  });
  const { mutate: completeClass, mutateAsync: completeClassAsync } = completeClassMutation;
  const lastCompletedClassRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isStudentSession || journeyReadOnly) return;
    if (!journeyId || !unitId || !classId) return;
    if (sortedBlocks.length === 0) return;
    const isFinalBlock =
      normalizedBlockIdx !== undefined &&
      normalizedBlockIdx === sortedBlocks.length - 1;
    if (!isFinalBlock) return;
    if (lastCompletedClassRef.current === classId) return;
    completeClass({ journeyId, unitId, classId });
    lastCompletedClassRef.current = classId;
  }, [classId, journeyId, unitId, normalizedBlockIdx, sortedBlocks.length, isStudentSession, journeyReadOnly, completeClass]);

  const exercisesRouter = trpc.exercises as any;
  const exercisesUtils = utils.exercises as any;

  const { mutateAsync: submitExerciseMutation } = trpc.exercises.submit.useMutation();

  const {
    exerciseFeedback,
    handleSubmit: submitExerciseHandler,
    seedInitialFeedback,
    clearFeedback,
  } = useExerciseManager({
    submitExercise: submitExerciseMutation,
    exercisesUtils,
  });

  const { hasPermission } = useAuthorization();

  const {
    data: classData,
    isLoading: isClassLoading,
  } = trpc.classes.get.useQuery(
    { id: classId },
    {
      enabled: Boolean(classId),
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const exerciseIds = useMemo(() => {
    const ids = new Set<string>();
    blocks.forEach((block) => {
      block.contents.forEach((content) => {
        if (content.type === ContentType.Exercise && content.exerciseId) {
          ids.add(content.exerciseId);
        }
      });
    });
    return Array.from(ids);
  }, [blocks]);

  const cleanSubmissionsMutation = trpc.exercises.cleanClassSubmissions.useMutation({
    onSuccess: () => {
      toast.success('Submissions cleaned for this class.');
      clearFeedback();

      if (!exerciseIds.length) {
        return;
      }

      exercisesUtils.getLatestSubmissions.invalidate({
        exerciseIds,
        studentId: viewerStudentId,
        includeHistory: false,
      });

      if (isTeacherView && viewerStudentId) {
        exercisesUtils.getSubmissionHistory.invalidate({
          exerciseIds,
          studentId: viewerStudentId,
        });
      }
    },
    onError(error) {
      const message =
        error instanceof Error ? error.message : 'Failed to clean submissions.';
      toast.error(message);
    },
  });

  const handleCleanSubmissions = useCallback(() => {
    cleanSubmissionsMutation.mutateAsync({ classId }).then(() => {
      window.location.reload();
    });
  }, [classId, cleanSubmissionsMutation]);

  const {
    data: latestSubmissionsData,
    isLoading: isLatestSubmissionsLoading,
  } = exercisesRouter.getLatestSubmissions.useQuery(
    { exerciseIds, studentId: viewerStudentId, includeHistory: false },
    {
      enabled: exerciseIds.length > 0,
      staleTime: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    data: submissionHistoryData,
    isLoading: isSubmissionHistoryLoading,
  } = exercisesRouter.getSubmissionHistory.useQuery(
    { exerciseIds, studentId: viewerStudentId },
    {
      enabled: exerciseIds.length > 0 && isTeacherView && Boolean(viewerStudentId),
      staleTime: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const latestSubmissionsByExerciseId = useMemo(() => {
    const submissions = (unwrapTrpcData(latestSubmissionsData) ?? []) as LatestSubmissionResult[];
    return submissions.reduce<Record<string, LatestSubmissionResult>>((acc, submission) => {
      acc[submission.exerciseId] = submission;
      return acc;
    }, {});
  }, [latestSubmissionsData]);

  const submissionHistoriesByExerciseId = useMemo(() => {
    if (!submissionHistoryData) {
      return {};
    }

    const historyEntries =
      unwrapTrpcData(submissionHistoryData) as { exerciseId: string; submissions: LatestSubmissionResult[] }[] | undefined;

    if (!historyEntries) {
      return {};
    }

    return historyEntries.reduce<Record<string, LatestSubmissionResult[]>>((acc, history) => {
      acc[history.exerciseId] = history.submissions ?? [];
      return acc;
    }, {});
  }, [submissionHistoryData]);

  useEffect(() => {
    seedInitialFeedback(latestSubmissionsByExerciseId);
  }, [latestSubmissionsByExerciseId, seedInitialFeedback]);

  usePageContext(useMemo(() => {
    if (!blocks || blocks.length === 0) return undefined;

    try {
      const contextPayload = blocks.map((block) => ({
        id: block.id,
        name: block.name,
        order: block.order,
        contents: block.contents.map((content) => {
          if (content.type === ContentType.Exercise && content.exerciseId) {
            return {
              ...content,
              latestSubmission: latestSubmissionsByExerciseId[content.exerciseId] ?? null,
            };
          }
          return content;
        }),
      }));

      return JSON.stringify(
        {
          classId,
          blocks: contextPayload,
        },
        null,
        2,
      );
    } catch (error) {
      console.error('Failed to serialize class detail context', error);
      return undefined;
    }
  }, [blocks, classId, latestSubmissionsByExerciseId]));

  // Next class in the unit → "Next issue…" teaser + CTA navigation target.
  const { data: nextClassData } = trpc.progress.getNextClass.useQuery(
    { journeyId: journeyId!, unitId: unitId!, currentClassId: classId },
    {
      enabled: isStudentSession && Boolean(journeyId) && Boolean(unitId),
      staleTime: Infinity,
    }
  );

  const nextClassId = nextClassData?.nextClassId ?? undefined;
  const { data: nextClassInfoData } = trpc.classes.get.useQuery(
    { id: nextClassId! },
    {
      enabled: Boolean(nextClassId),
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
  const nextClassInfo = nextClassInfoData as unknown as ClassSelect | null;

  const handleExerciseSubmit = useCallback(
    async (content: StudentExerciseContent, submission: ExerciseSubmission) => {
      if (!content.exerciseId) {
        toast.error('This exercise is missing required metadata. Please contact your instructor.');
        return;
      }

      await submitExerciseHandler(content, submission);
    },
    [submitExerciseHandler],
  );

  const { renderContent } = useContentRenderer({
    onExerciseSubmit: isReadOnly ? undefined : handleExerciseSubmit,
    exerciseFeedback,
    isReadOnly,
    viewerStudentId,
  });

  const isLoading =
    isClassLoading || isBlocksLoading || isLatestSubmissionsLoading || (isTeacherView && isSubmissionHistoryLoading);

  // Scroll to the ENTRY spread once the issue is on screen
  // (/classes/$classId/block/$blockIdx keeps working: same page, anchored).
  // Only the initial URL value scrolls — later blockIdx prop changes are our
  // own scroll→URL syncs and must not yank the reader around.
  const lastScrolledKeyRef = useRef<string | null>(null);
  const [initialExerciseId] = useState(scrollToExerciseId);
  // Exercise currently flashing its arrival ring (deep link landing).
  const [highlightedExerciseId, setHighlightedExerciseId] = useState<string | null>(null);
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const hasBlockTarget = initialBlockIdx !== undefined && initialBlockIdx > 0;
    if (!hasBlockTarget && !initialExerciseId) {
      return;
    }
    const key = `${classId}-${initialBlockIdx ?? 0}-${initialExerciseId ?? ''}`;
    if (lastScrolledKeyRef.current === key) {
      return;
    }
    lastScrolledKeyRef.current = key;

    const scrollToSpread = () => {
      if (hasBlockTarget) {
        document.getElementById(`s${initialBlockIdx + 1}`)?.scrollIntoView({ block: 'start' });
      }
    };

    if (!initialExerciseId) {
      const frame = requestAnimationFrame(scrollToSpread);
      return () => cancelAnimationFrame(frame);
    }

    // Deep link to one exercise (a teacher-comment notification). Land with
    // the teacher's note at the bottom of the view and the exercise above
    // it; without a note on the page, the exercise's top just below the
    // sticky navbar. Images and fonts above the target keep shifting the
    // layout for a moment after the first paint, so keep re-anchoring for a
    // short window instead of scrolling once; then flash the exercise so
    // the student sees where they were taken.
    const started = performance.now();
    let frame = 0;
    let highlightTimer = 0;
    const tick = () => {
      const exercise = document.getElementById(`exercise-${initialExerciseId}`);
      const note = exercise?.querySelector<HTMLElement>('[data-feedback-source="teacher"]') ?? null;
      const elapsed = performance.now() - started;
      if (!exercise) {
        if (elapsed < DEEP_LINK_WAIT_MS) {
          frame = requestAnimationFrame(tick);
        } else {
          scrollToSpread();
        }
        return;
      }
      if (note) {
        alignAtBottom(note, DEEP_LINK_GAP_PX);
      } else {
        alignBelowStickyBars(exercise, DEEP_LINK_GAP_PX);
      }
      if (elapsed < DEEP_LINK_SETTLE_MS) {
        frame = requestAnimationFrame(tick);
        return;
      }
      setHighlightedExerciseId(initialExerciseId);
      highlightTimer = window.setTimeout(() => setHighlightedExerciseId(null), DEEP_LINK_FLASH_MS);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(highlightTimer);
    };
  }, [isLoading, initialBlockIdx, initialExerciseId, classId]);

  const classInfo = classData as unknown as ClassSelect | null;

  // ── Cover / agenda derivations ────────────────────────────────────────────
  const wallJourney = wallData?.journey ?? null;
  const wallUnit = useMemo(
    () => wallData?.units?.find((unit) => unit.unitId === unitId) ?? null,
    [wallData, unitId],
  );
  const classNumberInUnit = useMemo(() => {
    if (!wallUnit) return undefined;
    const idx = wallUnit.classes.findIndex((cls) => cls.classId === classId);
    return idx >= 0 ? idx + 1 : undefined;
  }, [wallUnit, classId]);

  // The cartaz is a unit-level reward: the poster framing only appears on the
  // unit's final class. Without wall data we default to the plain next-class
  // back cover (never dangle a poster that can't be claimed).
  const isLastClassOfUnit = useMemo(() => {
    if (!wallUnit || wallUnit.classes.length === 0) return false;
    return wallUnit.classes[wallUnit.classes.length - 1]?.classId === classId;
  }, [wallUnit, classId]);

  const issueLine =
    wallUnit && classNumberInUnit !== undefined
      ? t('zine.issueLine', { n: classNumberInUnit, unit: wallUnit.name ?? '' })
      : wallUnit?.name ?? undefined;

  const mastheadRight = useMemo(() => {
    const parts = [wallJourney?.name, wallJourney?.level].filter(
      (part): part is string => Boolean(part && part.trim()),
    );
    return parts.length > 0 ? parts.join(' · ') : undefined;
  }, [wallJourney]);

  const totalMinutes = useMemo(
    () =>
      sortedBlocks.reduce(
        (sum, block) => sum + deriveBlockMinutes(block.contents),
        0,
      ),
    [sortedBlocks],
  );

  const stamps = useMemo<ZineStamp[]>(() => {
    if (sortedBlocks.length === 0) return [];
    return [
      { label: t('zine.minutes', { n: totalMinutes }), solid: true },
      { label: t('zine.activities', { count: sortedBlocks.length }) },
    ];
  }, [sortedBlocks.length, totalMinutes, t]);

  // Scroll, don't link: an `href="#s3"` resolves against the app's
  // `<base href="/">` and lands on the home page instead of the spread.
  const handleJumpToBlock = useCallback((index: number) => {
    document
      .getElementById(`s${index + 1}`)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  const agendaItems = useMemo<ZineAgendaItem[]>(
    () =>
      sortedBlocks.map((block, index) => {
        const kind = deriveBlockKind(block.contents);
        const kindLabel = t(`zine.kind.${kind}`);
        const description = block.description?.trim();
        // Kind already appears as the chip on the right — don't repeat it here.
        const subtitle = description
          ? truncate(description, AGENDA_SUBTITLE_MAX)
          : undefined;
        return {
          no: index + 1,
          title: block.name?.trim() || t('zine.untitledBlock', { n: index + 1 }),
          subtitle,
          kind: homeworkBlockIds.includes(block.id) ? `${t('zine.homework')} · ${kindLabel}` : kindLabel,
          minutes: t('zine.minutes', { n: deriveBlockMinutes(block.contents) }),
          done: deriveBlockDone(block.contents, latestSubmissionsByExerciseId),
          doneLabel: t('zine.done'),
          onClick: () => handleJumpToBlock(index),
        };
      }),
    [sortedBlocks, latestSubmissionsByExerciseId, t, handleJumpToBlock, homeworkBlockIds],
  );

  const navLabels = useMemo(
    () => ({
      nav: t('zine.nav.aria'),
      prev: t('zine.nav.prev'),
      next: t('zine.nav.next'),
      counter: t('zine.nav.counter', {
        n: zineZeroPad(currentBlockIdx + 1),
        total: zineZeroPad(sortedBlocks.length),
      }),
      open: t('zine.nav.open'),
      sheetTitle: t('zine.agendaLabel'),
      close: t('zine.nav.close'),
      now: t('zine.nav.now'),
    }),
    [t, currentBlockIdx, sortedBlocks.length],
  );

  // ── Back cover derivations ────────────────────────────────────────────────
  const earnedPosters = useMemo<BackCoverPoster[]>(
    () =>
      (wallData?.units ?? [])
        .filter((unit) => unit.status === 'completed')
        .map((unit) => ({
          text: truncate(unit.quote ?? unit.name ?? '', 48),
        })),
    [wallData],
  );

  const nextPosterLines = useMemo(() => {
    const lines =
      classNumberInUnit !== undefined
        ? [t('zine.wall.nextNo', { n: classNumberInUnit })]
        : [];
    return [...lines, t('zine.wall.nextYours'), t('zine.wall.nextNext')];
  }, [classNumberInUnit, t]);

  const teaser = useMemo(() => {
    if (!nextClassId || !nextClassInfo?.name) {
      return undefined;
    }
    const lead = wallUnit?.name
      ? t('zine.backCover.teaser', { unit: wallUnit.name })
      : t('zine.backCover.teaserNoUnit');
    return (
      <>
        {lead} <b>{nextClassInfo.name}</b>
      </>
    );
  }, [nextClassId, nextClassInfo, wallUnit, t]);

  const handleFinishClass = useCallback(async () => {
    if (!journeyId || !unitId || journeyReadOnly || completeClassMutation.isPending) {
      return;
    }
    try {
      const result = await completeClassAsync({ journeyId, unitId, classId });
      lastCompletedClassRef.current = classId;
      // SPA navigation keeps the scroll position — and the next class's back
      // cover looks identical to this one, so without a scroll reset the
      // click reads as "nothing happened". Land the student on the new cover.
      if (result.journeyComplete && onJourneyComplete) {
        // This class closed out the whole journey. The last unit's selo
        // ceremony must still play, so the host route sends the student to
        // the finished journey's wall (`?ceremony=`) and the celebration
        // (`?celebrate=`) takes over once the ceremony closes.
        onJourneyComplete({
          previousJourneyId: result.journeyComplete.previousJourneyId,
          nextJourneyId: result.journeyComplete.nextJourneyId,
          unitId,
        });
      } else if (nextClassId) {
        await navigate({
          to: `/classes/${nextClassId}/block/0` as string,
          search: { journeyId, unitId } as never,
        });
      } else {
        // No next class in this unit — the student just closed it out and
        // earned the cartaz. Hand the journey page the unit id so it can run
        // the selo ceremony on arrival; without it the ceremony stays behind a
        // manual "claim" click and finishing a unit reads as "nothing happened".
        await navigate({
          to: '/journey' as string,
          search: { ceremony: unitId } as never,
        });
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to complete the class.';
      toast.error(message);
    }
  }, [journeyId, unitId, classId, journeyReadOnly, completeClassMutation.isPending, completeClassAsync, nextClassId, navigate, onJourneyComplete]);

  const canCleanSubmissions = hasPermission('submissions:delete');
  const restartAction = canCleanSubmissions ? (
    <button
      type="button"
      className="z-restart"
      data-testid="restart-class-btn"
      title="Clear your answers for this class"
      disabled={!exerciseIds.length || cleanSubmissionsMutation.isPending}
      onClick={handleCleanSubmissions}
    >
      {t('class.restartClass')}
    </button>
  ) : undefined;

  const warningMessage =
    'Printing, exporting, or redistributing this English2Empower class content is prohibited.';

  if (isLoading) {
    return (
      <ProtectedContentShell
        contentClassName="relative min-h-screen"
        warningMessage={warningMessage}
        disableWatermark={true}
      >
        <ZineIssue className="min-h-screen">
          <style dangerouslySetInnerHTML={{ __html: ZINE_GLUE_CSS }} />
          <div className="z-page z-state-page">
            <p className="z-spread-eyebrow">
              {initialExerciseId ? t('zine.openingExercise') : t('zine.loading')}
            </p>
          </div>
        </ZineIssue>
      </ProtectedContentShell>
    );
  }

  if (!classInfo) {
    return (
      <ProtectedContentShell
        contentClassName="relative min-h-screen"
        warningMessage={warningMessage}
        disableWatermark={true}
      >
        <ZineIssue className="min-h-screen">
          <style dangerouslySetInnerHTML={{ __html: ZINE_GLUE_CSS }} />
          <div className="z-page z-state-page">
            <p>Class not found.</p>
          </div>
        </ZineIssue>
      </ProtectedContentShell>
    );
  }

  return (
    <ProtectedContentShell
      contentClassName="relative min-h-screen"
      warningMessage={warningMessage}
      disableWatermark={true}
    >
      <ZineIssue className="min-h-screen">
        <style dangerouslySetInnerHTML={{ __html: ZINE_GLUE_CSS }} />
        <ZineCover
          brand="ENGLISH2EMPOWER"
          mastheadRight={mastheadRight}
          issueLine={issueLine}
          title={classInfo.name || 'Untitled Class'}
          dek={classInfo.description || undefined}
          stamps={stamps}
          headerExtras={restartAction}
          imageUrl={classInfo.imageUrl}
          imageCaption={t('zine.coverPhotoCaption')}
          imageLabel={
            classNumberInUnit !== undefined
              ? t('zine.coverPhotoLabel', { n: classNumberInUnit })
              : undefined
          }
        />
        <AgendaBand
          label={t('zine.agendaLabel')}
          ariaLabel={t('zine.agendaLabel')}
          items={agendaItems}
        />
        <ClassBlockList
          blocks={sortedBlocks}
          latestSubmissionsByExerciseId={latestSubmissionsByExerciseId}
          submissionHistoriesByExerciseId={submissionHistoriesByExerciseId}
          renderContent={renderContent}
          mode={mode}
          highlightedExerciseId={highlightedExerciseId}
          homeworkBlockIds={homeworkBlockIds}
        />
        {sortedBlocks.length > 1 && (
          <BlockNavPill
            items={agendaItems}
            currentIndex={currentBlockIdx}
            onJump={handleJumpToBlock}
            hidden={agendaInView}
            labels={navLabels}
          />
        )}
        {isStudentSession && (
          <BackCover
            title={
              isLastClassOfUnit
                ? t('zine.backCover.title')
                : ""//t('zine.backCover.titleNextClass')
            }
            sub={journeyReadOnly ? t('class.journeyReadOnly') : undefined}
            posters={isLastClassOfUnit ? earnedPosters : []}
            nextPosterLines={isLastClassOfUnit ? nextPosterLines : undefined}
            ctaLabel={
              completeClassMutation.isPending
                ? t('zine.backCover.ctaPending')
                : isLastClassOfUnit
                  ? t('zine.backCover.cta')
                  : t('zine.backCover.ctaNextClass')
            }
            onCta={handleFinishClass}
            ctaDisabled={!journeyId || !unitId || journeyReadOnly || completeClassMutation.isPending}
            hideCta={journeyReadOnly}
            teaser={teaser}
            wallAriaLabel={t('zine.wall.ariaLabel')}
          />
        )}
      </ZineIssue>
    </ProtectedContentShell>
  );
}
